import {
  List,
  Transition,
  CloseButton,
  ActionIcon,
  Flex,
  Divider,
  Text,
  useMantineTheme,
  Badge,
} from "@mantine/core";
import { AttendanceStatus } from "src/generated/graphql";
import { IconDotsVertical } from "@tabler/icons";
import {
  RiCheckLine,
  RiEyeCloseLine,
  RiUpload2Line,
  RiDownload2Line,
} from "react-icons/ri";
import { memo, useState } from "react";
import { useClickOutside } from "@mantine/hooks";
import { ReactNode } from "react";
import { IoIosRemoveCircle } from "react-icons/io";

export type IListObj = {
  id: string;
  name: string;
  clientId?: string;
  checkForClient?: boolean;
  attendanceStatus?: AttendanceStatus;
  isPaid?: boolean;
};

export type IActiveOption = { name: string; id: string } | undefined;

type IProps = {
  options: IListObj[];
  isAttendee: boolean;

  onCancel: ({ id, name }: IListObj) => void;
  onUpdateWaitList: ({ id, name }: IListObj) => void;
  onUpdateAttendanceStatus?: (
    { id, name }: IListObj,
    status: AttendanceStatus
  ) => void;

  delIcon?: ReactNode;
  editIcon?: ReactNode;
  noShowIcon?: ReactNode;
  cancelIcon?: ReactNode;
  isWaitlistEnabled?: boolean;
  waitlistLoading?: boolean;
  cancelLoading?: boolean;
  noShowLoading?: boolean;
  CheckInLoading?: boolean;

  showEdit?: boolean;
  showCancel?: boolean;
  showNoShow?: boolean;
  showCheckIn?: boolean;
  isAllowWrite: boolean;
  activeOption: IActiveOption;
  setActiveOption: (e: IActiveOption) => void;
};

function ListDelete({
  options = [],
  onUpdateWaitList,
  onCancel,
  onUpdateAttendanceStatus,

  isAttendee,
  isWaitlistEnabled,

  waitlistLoading = false,
  cancelLoading = false,
  noShowLoading = false,
  CheckInLoading = false,

  showEdit = false,
  showCancel = true,
  showNoShow = false,
  showCheckIn = false,
  isAllowWrite,
  activeOption,
  setActiveOption,
}: IProps) {
  const theme = useMantineTheme();
  let color = theme.colors.secondary[4];

  const outsideClickRef = useClickOutside(() => {
    setActiveOption(undefined);
  });

  const handleOpenListMenu = (option: { name: string; id: string }) => {
    setActiveOption(option);
  };

  return (
    <List w="100%" listStyleType="none" style={{ color }}>
      {options.map((item, index) => (
        <List.Item
          icon={null}
          key={item.id}
          c={color}
          sx={{
            "&": {
              position: "relative",
            },
            "& ::marker": {
              content: "none",
            },
            "& .mantine-List-itemWrapper": {
              display: "flex",
            },
            "& .mantine-List-itemWrapper > span > div": {
              alignItems: "center",
            },
          }}
        >
          {!!index && (
            <Divider style={{ borderColor: theme.colors.secondary[0] }} />
          )}
          <Flex align="center" justify="space-between" my={2}>
            <Text m={10} c={theme.colors.secondaryTextColor[6]}>
              {item.name as string}
            </Text>
            {isAllowWrite &&
            item.attendanceStatus &&
            item.attendanceStatus !== AttendanceStatus.Upcoming ? (
              <Badge
                size="md"
                mt={2}
                sx={(theme) => ({
                  background: theme.colors.primary[6],
                  color: theme.colors.primaryTextColor[6],
                  textTransform: "none",
                })}
              >
                {item.attendanceStatus}
              </Badge>
            ) : (
              <ActionIcon
                size="lg"
                mr={5}
                c={theme.colors.light[5]}
                onClick={() => handleOpenListMenu(item)}
                sx={{
                  "&:hover": {
                    backgroundColor: theme.colors.secondary[4],
                  },
                }}
              >
                <IconDotsVertical />
              </ActionIcon>
            )}
          </Flex>
          <Transition
            mounted={activeOption?.id === item.id}
            transition="fade"
            duration={200}
            timingFunction="ease-in-out"
          >
            {(styles) => (
              <Flex
                my={3}
                justify="flex-end"
                bg={theme.colors.secondary[4]}
                style={{
                  ...styles,
                  position: "absolute",
                  inset: 0,
                  borderRadius: 4,
                }}
                h={42}
                ref={outsideClickRef}
              >
                {showCheckIn && isAttendee ? (
                  <ActionIcon
                    variant="light"
                    size="lg"
                    my={4}
                    w="120px"
                    onClick={() =>
                      onUpdateAttendanceStatus &&
                      onUpdateAttendanceStatus(item, AttendanceStatus.Attended)
                    }
                    loading={CheckInLoading}
                    sx={{
                      backgroundColor: theme.colors.secondary[4],
                      "&:hover": {
                        backgroundColor: theme.colors.secondary[6],
                      },
                    }}
                  >
                    <>
                      <RiCheckLine
                        size={17}
                        color={theme.colors.secondaryTextColor[4]}
                      />
                      <Text
                        sx={{ fontSize: 13 }}
                        pl={5}
                        color={theme.colors.secondaryTextColor[4]}
                      >
                        Check In
                      </Text>
                    </>
                  </ActionIcon>
                ) : null}

                {showNoShow && isAttendee ? (
                  <ActionIcon
                    variant="light"
                    size="lg"
                    my={4}
                    w="120px"
                    onClick={() =>
                      onUpdateAttendanceStatus &&
                      onUpdateAttendanceStatus(item, AttendanceStatus.Missed)
                    }
                    loading={noShowLoading}
                    sx={{
                      backgroundColor: theme.colors.secondary[4],
                      "&:hover": {
                        backgroundColor: theme.colors.secondary[6],
                      },
                    }}
                  >
                    <>
                      <RiEyeCloseLine
                        size={17}
                        color={theme.colors.secondaryTextColor[4]}
                      />
                      <Text
                        sx={{ fontSize: 13 }}
                        pl={5}
                        color={theme.colors.secondaryTextColor[4]}
                      >
                        No Show
                      </Text>
                    </>
                  </ActionIcon>
                ) : null}

                {(showEdit && isAttendee && isWaitlistEnabled) ||
                !isAttendee ? (
                  <ActionIcon
                    variant="light"
                    size="lg"
                    my={4}
                    w="120px"
                    onClick={() => onUpdateWaitList(item)}
                    loading={waitlistLoading}
                    sx={{
                      backgroundColor: theme.colors.secondary[4],
                      "&:hover": {
                        backgroundColor: theme.colors.secondary[6],
                      },
                    }}
                  >
                    <>
                      {isAttendee ? (
                        <RiDownload2Line
                          size={17}
                          color={theme.colors.secondaryTextColor[4]}
                        />
                      ) : (
                        <RiUpload2Line
                          size={17}
                          color={theme.colors.secondaryTextColor[4]}
                        />
                      )}
                      <Text
                        sx={{ fontSize: 13 }}
                        pl={5}
                        color={theme.colors.secondaryTextColor[4]}
                      >
                        Move to {isAttendee ? "Waitlist" : "Attendees"}
                      </Text>
                    </>
                  </ActionIcon>
                ) : null}

                {showCancel ? (
                  <ActionIcon
                    variant="light"
                    color="red.4"
                    size="lg"
                    my={4}
                    w="100px"
                    onClick={() => onCancel(item)}
                    sx={{
                      backgroundColor: theme.colors.secondary[4],
                      "&:hover": {
                        backgroundColor: theme.colors.secondary[6],
                      },
                    }}
                    loading={cancelLoading}
                  >
                    {
                      <>
                        <IoIosRemoveCircle
                          color={theme.colors.red[6]}
                          size={20}
                        />
                        <Text sx={{ fontSize: 13 }} pl={3}>
                          Cancel
                        </Text>
                      </>
                    }
                  </ActionIcon>
                ) : null}

                <CloseButton
                  size="lg"
                  c={theme.colors.light[5]}
                  onClick={() => setActiveOption(undefined)}
                  my={4}
                  mx={5}
                  sx={{
                    "&:hover": {
                      backgroundColor: theme.colors.secondary[6],
                    },
                  }}
                />
              </Flex>
            )}
          </Transition>
        </List.Item>
      ))}
    </List>
  );
}

export default memo(ListDelete);
